// src/components/DockedCallBar.jsx
import React, { useMemo, useEffect, useState, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Icon,
  LoaderCircle,
} from 'rhinostyle';
import { useVoip } from '../voip/VoipProvider';
import { formatPhone } from '../helpers/PhoneHelpers';
import { fetchUser } from '../reducers/userReducer';

const ContactProfilePanel = React.lazy(() => import('./ContactProfilePanel'));

class CallPanelErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.warn('Call panel profile error:', error.message); // eslint-disable-line no-console
  }

  render() {
    if (this.state.hasError) {
      return <p className="u-text-center u-text-muted u-p-a">Unable to load contact profile</p>;
    }
    return this.props.children; // eslint-disable-line react/prop-types
  }
}

const DockedCallBar = () => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(true);
  const [callDuration, setCallDuration] = useState(0);

  const {
    callPhase,
    incomingCall,
    activeCall,
    answer,
    reject,
    hangup,
    toggleMute,
    toggleHold,
    isMuted,
    isHeld,
    callContact,
  } = useVoip();

  const currentCall = incomingCall || activeCall;
  const hasCall = currentCall && callPhase !== 'idle' && callPhase !== 'ended';

  const contactId = callContact?.id;

  // Load full user into Redux store
  const allUsers = useSelector((state) => state.user.users);
  useEffect(() => {
    if (contactId) {
      // Only fetch if user isn't already fully loaded in Redux
      if (!allUsers[contactId]?.organization) {
        dispatch(fetchUser(contactId));
      }
    }
  }, [contactId, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Call duration timer
  useEffect(() => {
    let interval;
    if (callPhase === 'active') {
      setCallDuration(0);
      interval = setInterval(() => setCallDuration((prev) => prev + 1), 1000);
    } else {
      setCallDuration(0);
    }
    return () => clearInterval(interval);
  }, [callPhase]);

  // Auto-expand on new call
  useEffect(() => {
    if (hasCall) setIsExpanded(true);
  }, [hasCall]);

  // Check if the full user is loaded in Redux
  const fullUser = useSelector((state) => (contactId ? state.user.users[contactId] : null));
  const isFullUserLoaded = fullUser && fullUser.organization !== undefined;

  const contactName = callContact
    ? [callContact.firstName, callContact.lastName].filter(Boolean).join(' ')
    : null;

  const callerDisplay =
    contactName ||
    currentCall?.remoteIdentity?.displayName ||
    currentCall?._remoteDisplayName ||
    currentCall?._outgoingTarget ||
    'Unknown';

  const callerNumber = useMemo(() => {
    if (currentCall?._outgoingTarget) {
      return currentCall._outgoingTarget;
    }
    const callerUri = currentCall?.remoteIdentity?.uri?.toString() || '';
    return callerUri.replace(/^sip:([^@]+)@.*$/, '$1');
  }, [currentCall]);

  const phaseLabel = callPhase === 'ringing' ? 'Incoming Call' : 'Active Call';
  const formattedNumber = formatPhone(callerNumber) || callerNumber;

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!hasCall) return null;

  // Minimized bottom bar
  if (!isExpanded) {
    return (
      <>
        <div className="call-bar-mini">
          <div className="call-bar-mini__left" onClick={() => setIsExpanded(true)}>
            <span className="call-bar-mini__indicator" />
            <div className="call-bar-mini__info">
              <span className="call-bar-mini__name">{callerDisplay}</span>
              {callerNumber && callerDisplay !== callerNumber && (
                <span className="call-bar-mini__number">{formattedNumber}</span>
              )}
            </div>
            {callPhase === 'active' && (
              <span className="call-bar-mini__duration">{formatDuration(callDuration)}</span>
            )}
            {callPhase === 'ringing' && (
              <span className="call-bar-mini__ringing">Ringing...</span>
            )}
          </div>
          <div className="call-bar-mini__actions">
            {callPhase === 'ringing' && (
              <>
                <Button type="secondary" size="small" onClick={answer}>Answer</Button>
                <Button type="danger" size="small" onClick={reject}>Reject</Button>
              </>
            )}
            {callPhase === 'active' && (
              <>
                <Button
                  type={isMuted ? 'default' : 'input'}
                  size="small"
                  onClick={toggleMute}
                >
                  {isMuted ? 'Unmute' : 'Mute'}
                </Button>
                <Button type="danger" size="small" onClick={hangup}>End</Button>
              </>
            )}
            <Button type="link" size="small" onClick={() => setIsExpanded(true)}>
              <Icon icon="caret-up" />
            </Button>
          </div>
        </div>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio id="remoteAudio" autoPlay playsInline />
      </>
    );
  }

  // Expanded right-side panel
  return (
    <>
      <div className="call-panel is-open">
        {/* Header */}
        <div className="call-panel__header">
          <span className="call-panel__header__phase">
            {phaseLabel}
            {callPhase === 'active' && (
              <span className="call-panel__header__duration"> — {formatDuration(callDuration)}</span>
            )}
          </span>
          <Button type="link" iconOnly size="small" onClick={() => setIsExpanded(false)} className="call-panel__header__close">
            <Icon icon="close" />
          </Button>
        </div>

        {/* Full Contact Profile Panel */}
        {contactId && isFullUserLoaded && (
          <div className="call-panel__profile">
            <CallPanelErrorBoundary>
              <Suspense fallback={<div className="u-text-center u-p-a"><LoaderCircle size="small" /></div>}>
                <ContactProfilePanel
                  activeUser={fullUser}
                  isActivePanel
                  showGoToConversation
                  setOpenContactLibraryPanel={() => {}}
                />
              </Suspense>
            </CallPanelErrorBoundary>
          </div>
        )}

        {contactId && !isFullUserLoaded && (
          <div className="u-text-center u-p-a"><LoaderCircle size="small" /></div>
        )}

        {!contactId && (
          <div className="call-panel__no-contact">
            <p className="u-text-center u-text-muted u-p-a">No contact found for this number</p>
          </div>
        )}

        {/* Call Controls */}
        <div className="call-panel__controls">
          {callPhase === 'ringing' ? (
            <>
              <Button type="secondary" onClick={answer} className="call-panel__btn">
                <Icon icon="phone" /> Answer
              </Button>
              <Button type="danger" onClick={reject} className="call-panel__btn">
                <Icon icon="phone" /> Reject
              </Button>
            </>
          ) : (
            <>
              <Button
                type={isMuted ? 'default' : 'input'}
                onClick={toggleMute}
                className="call-panel__btn"
              >
                {isMuted ? 'Unmute' : 'Mute'}
              </Button>
              <Button
                type={isHeld ? 'default' : 'input'}
                onClick={toggleHold}
                className="call-panel__btn"
              >
                {isHeld ? 'Resume' : 'Hold'}
              </Button>
              <Button type="danger" onClick={hangup} className="call-panel__btn">
                Hang Up
              </Button>
            </>
          )}
        </div>

        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio id="remoteAudio" autoPlay playsInline />
      </div>
    </>
  );
};

export default DockedCallBar;
